import React, { useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';
import SmoothCollapse from 'react-smooth-collapse';
import ChevronDownIcon from 'calcite-ui-icons-react/ChevronDownIcon';
import Select from '../select';

import MapController from '../../../../controllers/MapController';

import { RootState } from '../../../../reducers/app';
import config from '../../../../config/config';

import './my-layer.scss';
import LayerTooltip from './layer-tooltip';
import InfoSvg from '../../../../assets/info-icon';
import { IOptionsType } from '../../../../types/react-select-options';
import { IRasterFunctionInfo } from '../../../../types/layers';

const MapLayer = ({ layer }: { layer: any }) => {
  // LOCAL STATE
  const [sectionOpen, setSectionOpen] = useState<boolean>(false);
  const [selectedRenderingOption, setSelectedRenderingOption] = useState<IOptionsType>({
    value: 'Natural Color with DRA',
    label: 'Natural Color with DRA',
  });
  const [renderingOptions, setRenderingOptions] = useState<Array<IOptionsType>>([]);

  // REDUX
  const visibleLayers = useSelector((state: RootState) => state.mapview.visibleLayers);
  const disabledLayers = useSelector((state: RootState) => state.mapview.disabledLayers);

  // REFS
  const legendContainerRef = React.createRef<any>();

  useEffect(() => {
    if (layer.id === 'sentinel' || layer.id === 'landsat') {
      MapController.updateRenderingOption(layer.id, selectedRenderingOption.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRenderingOption]);

  useEffect(() => {
    MapController.fetchLegendInfo(legendContainerRef.current, layer.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (layer.id === 'landsat' || layer.id === 'sentinel') {
      const fetchLayerJson = async () => {
        const layerConfig = config.layers.find((configLayer) => configLayer.options.id === layer.id);
        if (layerConfig) {
          const { options, renderingRules } = layerConfig;
          const res = await fetch(`${options.url}/?f=pjson`);
          const body = await res.json();
          const rasterFunctionInfos = body.rasterFunctionInfos?.map((rf: IRasterFunctionInfo) => rf.name);
          if (renderingRules && rasterFunctionInfos.length > 0) {
            const availableRenderingRules = renderingRules?.filter((rule) => rasterFunctionInfos.includes(rule));
            setRenderingOptions(
              availableRenderingRules?.map((option: string) => {
                return { value: option, label: option };
              })
            );
          }
        }
      };
      fetchLayerJson();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='layer-container' key={layer.id}>
      <div data-tip data-tip-disable={!disabledLayers.includes(layer.id)} data-for='disabled-layer-tooltip' className={`layer ${disabledLayers.includes(layer.id) ? 'layer-disabled' : ''}`}>
        <div className='layer-title__wrapper'>
          <input
            type='checkbox'
            id={layer.id}
            name={layer.id}
            disabled={disabledLayers.includes(layer.id) }
            checked={visibleLayers.includes(layer.id)}
            onChange={() => MapController.updateLayerVisibility(layer.id, !visibleLayers.includes(layer.id))}
          />
          <div className='layer-title'>{layer.title}</div>
        </div>
        <div className='tools'>
          {layer.description && layer.description.length > 0 && (
            <LayerTooltip tip={layer.description} disabled={disabledLayers.includes(layer.id) }>
              <InfoSvg />
            </LayerTooltip>
            
          )}
          <ChevronDownIcon
            className={sectionOpen ? 'open' : ''}
            size={24}
            onClick={() => setSectionOpen(disabledLayers.includes(layer.id)  ? false : !sectionOpen)}
          />
        </div>
        <ReactTooltip className='tooltip' id='tooltip' place='right' type='success' effect='solid' clickable={true} />
      </div>
      {disabledLayers.includes(layer.id) &&
        <ReactTooltip 
          className='tooltip' 
          id='disabled-layer-tooltip' 
          place='right' 
          type='success' 
          effect='solid'>
          <span>Zoom in to enable {layer.title}</span>
        </ReactTooltip>
      }
      <SmoothCollapse className='hansen-container' expanded={sectionOpen} allowOverflowWhenOpen eagerRender={true}>
        {layer.id !== 'sentinel' && layer.id !== 'landsat' ? (
          <div ref={legendContainerRef} />
        ) : (
          renderingOptions.length > 0 && (
            <>
              <div className='rendering-rule-container'>
                
                <Select
                  isMulti={false}
                  options={renderingOptions}
                  value={selectedRenderingOption}
                  onChange={setSelectedRenderingOption}
                  isDisabled={false}
                  isClearable={false}
                  placeholder='Select Rendering Option'
                />
                <LayerTooltip 
                  id="rendering-tip" 
                  tip={layer.id === 'landsat' ? config.helpertext.landsatRenderingRuleInfo : config.helpertext.sentinelRenderingRuleInfo}>
                  <InfoSvg />
                </LayerTooltip>
                <ReactTooltip 
                  className='tooltip' 
                  id='rendering-tip' 
                  place='right' 
                  type='success' 
                  effect='solid'
                  clickable={true} />
              </div>
            </>
          )
        )}
      </SmoothCollapse>
    </div>
  );
};

export default MapLayer;
